/**
 * L1 Field Demo Component
 *
 * Example usage of the useL1Field hook for testing and demonstration.
 *
 * FERP-PII-052: Demonstrates user-controlled encryption for L1 fields.
 *
 * Usage:
 * 1. Navigate to /pii-demo in the portal
 * 2. Enter a field name (e.g., "ssn") and value
 * 3. Click "Store Field" - prompts for biometric
 * 4. Click "Get Field" - prompts for biometric to decrypt
 */

import { useState } from 'react';
import {
  Container,
  Title,
  Text,
  TextInput,
  Button,
  Stack,
  Alert,
  Paper,
  Group,
  Code,
  Badge,
  LoadingOverlay,
} from '@mantine/core';
import { IconFingerprint, IconLock, IconLockOpen, IconTrash, IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { useL1Field } from './useL1Field';

export function L1FieldDemo() {
  const [fieldName, setFieldName] = useState('ssn');
  const [fieldValue, setFieldValue] = useState('');
  const [retrievedValue, setRetrievedValue] = useState<string | null>(null);
  const [fieldExists, setFieldExists] = useState<boolean | null>(null);
  const [lastResult, setLastResult] = useState<{ type: string; success: boolean; message: string } | null>(null);

  const {
    storeField,
    getField,
    hasField,
    deleteField,
    authenticatePrf,
    prfOutput,
    loading,
    error,
    clearError,
    clearPrfOutput,
  } = useL1Field();

  const handleStore = async () => {
    clearError();
    setLastResult(null);

    const result = await storeField(fieldName, fieldValue);
    setLastResult({
      type: 'store',
      success: result.success,
      message: result.success
        ? `Field "${fieldName}" encrypted and stored`
        : result.error || 'Failed to store field',
    });

    if (result.success) {
      setFieldValue(''); // Clear input after successful store
    }
  };

  const handleGet = async () => {
    clearError();
    setLastResult(null);
    setRetrievedValue(null);

    const result = await getField(fieldName);
    setLastResult({
      type: 'get',
      success: result.success,
      message: result.success
        ? `Field "${fieldName}" decrypted successfully`
        : result.error || 'Failed to retrieve field',
    });

    if (result.success && result.value) {
      setRetrievedValue(result.value);
    }
  };

  const handleCheck = async () => {
    clearError();
    setLastResult(null);
    setFieldExists(null);

    const exists = await hasField(fieldName);
    setFieldExists(exists);
    setLastResult({
      type: 'check',
      success: true,
      message: exists
        ? `Field "${fieldName}" exists`
        : `Field "${fieldName}" does not exist`,
    });
  };

  const handleDelete = async () => {
    clearError();
    setLastResult(null);

    const success = await deleteField(fieldName);
    setLastResult({
      type: 'delete',
      success,
      message: success
        ? `Field "${fieldName}" deleted (GDPR erasure)`
        : 'Failed to delete field',
    });

    if (success) {
      setRetrievedValue(null);
      setFieldExists(null);
    }
  };

  const handlePreAuth = async () => {
    clearError();
    setLastResult(null);

    const output = await authenticatePrf();
    setLastResult({
      type: 'auth',
      success: !!output,
      message: output
        ? 'PRF authentication successful - cached for subsequent operations'
        : 'PRF authentication failed',
    });
  };

  return (
    <Container size="sm" py="xl">
      <LoadingOverlay visible={loading} />

      <Stack gap="lg">
        <div>
          <Title order={2}>L1 Field Encryption Demo</Title>
          <Text c="dimmed" size="sm">
            Test user-controlled encryption for L1 (critical financial) fields
          </Text>
        </div>

        {/* PRF Status */}
        <Paper p="md" withBorder>
          <Group justify="space-between" ta="center">
            <div>
              <Text fw={500}>PRF Authentication Status</Text>
              <Text size="xs" c="dimmed">
                PRF output is used to derive encryption keys
              </Text>
            </div>
            <Group gap="sm">
              <Badge color={prfOutput ? 'green' : 'gray'} leftSection={<IconFingerprint size={12} />}>
                {prfOutput ? 'Authenticated' : 'Not Authenticated'}
              </Badge>
              {prfOutput && (
                <Button size="xs" variant="light" color="red" onClick={clearPrfOutput}>
                  Clear
                </Button>
              )}
            </Group>
          </Group>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert color="red" icon={<IconAlertCircle />} onClose={clearError} withCloseButton>
            {error}
          </Alert>
        )}

        {/* Result Alert */}
        {lastResult && (
          <Alert
            color={lastResult.success ? 'green' : 'red'}
            icon={lastResult.success ? <IconCheck /> : <IconAlertCircle />}
          >
            <Text fw={500}>{lastResult.type.toUpperCase()}</Text>
            <Text size="sm">{lastResult.message}</Text>
          </Alert>
        )}

        {/* Field Name Input */}
        <TextInput
          label="Field Name"
          description="L1 field identifier (e.g., ssn, bank_account_number, tin)"
          placeholder="ssn"
          value={fieldName}
          onChange={(e) => setFieldName(e.currentTarget.value)}
        />

        {/* Field Value Input */}
        <TextInput
          label="Field Value"
          description="Plaintext value to encrypt (only for storing)"
          placeholder="123-45-6789"
          value={fieldValue}
          onChange={(e) => setFieldValue(e.currentTarget.value)}
        />

        {/* Action Buttons */}
        <Group grow>
          <Button
            leftSection={<IconLock size={16} />}
            onClick={handleStore}
            disabled={!fieldName || !fieldValue}
            color="blue"
          >
            Store Field
          </Button>
          <Button
            leftSection={<IconLockOpen size={16} />}
            onClick={handleGet}
            disabled={!fieldName}
            color="green"
          >
            Get Field
          </Button>
        </Group>

        <Group grow>
          <Button
            leftSection={<IconFingerprint size={16} />}
            onClick={handleCheck}
            disabled={!fieldName}
            variant="light"
          >
            Check Exists
          </Button>
          <Button
            leftSection={<IconTrash size={16} />}
            onClick={handleDelete}
            disabled={!fieldName}
            variant="light"
            color="red"
          >
            Delete Field
          </Button>
        </Group>

        <Button
          leftSection={<IconFingerprint size={16} />}
          onClick={handlePreAuth}
          variant="outline"
          fullWidth
        >
          Pre-Authenticate (Cache PRF)
        </Button>

        {/* Retrieved Value Display */}
        {retrievedValue !== null && (
          <Paper p="md" withBorder bg="gray.0">
            <Text fw={500} mb="xs">
              Decrypted Value:
            </Text>
            <Code block>{retrievedValue}</Code>
          </Paper>
        )}

        {/* Field Exists Display */}
        {fieldExists !== null && (
          <Paper p="md" withBorder>
            <Group>
              <Text fw={500}>Field Exists:</Text>
              <Badge color={fieldExists ? 'green' : 'gray'}>
                {fieldExists ? 'Yes' : 'No'}
              </Badge>
            </Group>
          </Paper>
        )}

        {/* Info */}
        <Paper p="md" withBorder bg="blue.0">
          <Text size="sm" fw={500} mb="xs">
            Security Notes
          </Text>
          <Text size="xs" c="dimmed">
            - L1 fields (SSN, bank accounts) are encrypted with YOUR biometric{'\n'}
            - No one else can decrypt - not admins, not guardians, not break-glass{'\n'}
            - PRF output is never stored, only used in-memory{'\n'}
            - Each field gets a unique encryption key via HKDF
          </Text>
        </Paper>
      </Stack>
    </Container>
  );
}
